import { API_URL, getAccessToken } from "@/api/_client";
import { ChatMessageType, useChatHistory } from "@/api/chat";
import { WS_RECONNECT } from "@/lib/ws-reconnect";
import React, { useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useAppContext } from "./app-context";
import { ChatMessagesContext } from "./chat-message-provider";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/api/user";
import { isUnseenSocketMessage, mergeChatMessages } from "./chat-messages";

const CHAT_WEBSOCKET_URL = API_URL + "ws/chat";

// Just past the backend's 120s AI_RESPONSE_TIMEOUT. Was 10s, which killed the
// "thinking" dots after 10 seconds even though a doc-chat AI answer can take up
// to two minutes — so the chat looked frozen mid-answer. The backend sends a
// single is_typing:true at the start and is_typing:false on completion, so this
// is just the safety cap that keeps the dots alive until the answer lands.
// Mirrors the agent provider's TYPING_TIMEOUT_DURATION.
const TYPING_TIMEOUT_DURATION = 130 * 1000;

export const ChatMessagesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currentChatSessionId, useConfirmation } = useAppContext();
  // useConfirmation() returns a fresh object each render, so keep the latest in a
  // ref and read it inside the WS effect — otherwise the effect would have to
  // list an unstable value in its deps and re-subscribe on every render.
  const confirmation = useConfirmation();
  const confirmationRef = React.useRef(confirmation);
  confirmationRef.current = confirmation;
  const queryClient = useQueryClient();
  const [usersTyping, setUsersTyping] = React.useState<
    { first_name: string; last_name: string; id: string }[]
  >([]);
  const user = useUser();
  // History = server state → React Query (loading/error/retry for free, and it
  // no longer duplicates a server resource into useState). Live WebSocket
  // messages are ephemeral events, so they stay in local state and get merged
  // on top of the fetched history.
  const { data: historyMessages } = useChatHistory(currentChatSessionId);
  const [liveMessages, setLiveMessages] = React.useState<ChatMessageType[]>([]);
  // A session-id change reruns the message effect while react-use-websocket may
  // still expose the previous socket's last MessageEvent. Remember the exact
  // frame already consumed so dependency changes cannot append it to the newly
  // selected chat.
  const lastHandledMessageRef = React.useRef<MessageEvent | null>(null);
  const messages = React.useMemo(
    () => mergeChatMessages(historyMessages ?? [], liveMessages),
    [historyMessages, liveMessages],
  );
  const typingTimeouts = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeUserFromTyping = useCallback((userId: string) => {
    const existingTimeout = typingTimeouts.current.get(userId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      typingTimeouts.current.delete(userId);
    }
    setUsersTyping((oldUsers) => oldUsers.filter((user) => user.id !== userId));
  }, []);

  const token = getAccessToken();
  const url = new URL(CHAT_WEBSOCKET_URL);
  url.searchParams.append("session_id", currentChatSessionId || "");
  url.searchParams.append("token", token || "");

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    url.href,
    {
      shouldReconnect: () =>
        currentChatSessionId !== undefined && token !== undefined,
      ...WS_RECONNECT,
    },
    currentChatSessionId !== undefined && token !== undefined,
  );

  const submitEvent = useCallback(
    (event: string | object) => {
      if (currentChatSessionId && token) {
        sendJsonMessage({ event: event });
      }
    },
    [currentChatSessionId, token, sendJsonMessage],
  );

  React.useEffect(() => {
    if (!currentChatSessionId) return;
    // New session: drop the previous session's live buffer (useChatHistory
    // refetches the new session's history on its own).
    setLiveMessages([]);

    // Cleanup: clear all typing timeouts when chat session changes
    const timeouts = typingTimeouts.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      setUsersTyping([]);
    };
  }, [currentChatSessionId]);

  const canSendMessages = readyState === ReadyState.OPEN;

  React.useEffect(() => {
    if (!lastMessage || !lastMessage.data) return;
    if (!isUnseenSocketMessage(lastMessage, lastHandledMessageRef.current)) return;
    lastHandledMessageRef.current = lastMessage;
    // Parse the last message data and handle different message types
    const messageData = JSON.parse(lastMessage.data);
    // if the user is "typing" and just received a message, clear their typing status
    if (messageData.type === "chat_message") {
      // Optional chaining: AI/error frames may carry user: {id: null}; a missing
      // user must not crash the whole chat page (defense-in-depth alongside the
      // backend frames now always including a user).
      removeUserFromTyping(messageData.user?.id);
    }
    if (
      messageData.sender === "event" &&
      "is_typing" in (messageData.events || {})
    ) {
      // Handle typing events
      if (messageData.events.is_typing) {
        if (messageData.user.id === user.data?.id) {
          // If the user is typing, we don't want to add them to the typing list
          return;
        }

        // Clear any existing timeout for this user
        const existingTimeout = typingTimeouts.current.get(messageData.user.id);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        setUsersTyping((oldUsers) => {
          if (!oldUsers.map((ou) => ou.id).includes(messageData.user.id)) {
            return [...oldUsers, messageData.user];
          }
          return oldUsers;
        });

        const timeout = setTimeout(() => {
          removeUserFromTyping(messageData.user.id);
        }, TYPING_TIMEOUT_DURATION);

        typingTimeouts.current.set(messageData.user.id, timeout);
      } else {
        removeUserFromTyping(messageData.user.id);
      }
      return;
    }
    // Handle user removed event
    if (
      messageData.type === "event" &&
      messageData.events.user_removed?.length
    ) {
      messageData.events.user_removed.forEach(
        (userItem: { item: { id: string } }) => {
          if (userItem.item.id === user.data?.id) {
            // Removed from the chat: empty the view (clear the live buffer and
            // the cached history for this session).
            setLiveMessages([]);
            queryClient.setQueryData(
              ["chat-history", currentChatSessionId],
              [],
            );
            queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
            confirmationRef.current.open({
              title: "You have been removed from the chat",
              description: "You can no longer send messages in this chat.",
              confirmText: "Ok",
              hideCancel: true,
            });
          }
        },
      );
    }
    // handle messages — live WS messages append to the live buffer; the merge
    // dedups them against history by id.
    setLiveMessages((oldMessages) => [...oldMessages, messageData]);
    if (
      messageData.type === "event" &&
      (messageData.events.users_added?.length ||
        messageData.events.users_removed?.length)
    ) {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    }
  }, [
    lastMessage,
    queryClient,
    user.data?.id,
    removeUserFromTyping,
    currentChatSessionId,
  ]);

  const sendMessage = useCallback(
    (message: string, useAI: boolean) => {
      if (canSendMessages) sendJsonMessage({ message: message, use_ai: useAI });
    },
    [canSendMessages, sendJsonMessage],
  );

  return (
    <ChatMessagesContext.Provider
      value={{
        canSendMessages,
        sendMessage,
        messages,
        submitEvent,
        usersTyping,
      }}
    >
      {children}
    </ChatMessagesContext.Provider>
  );
};
